/**
 * api/knowledgeApi.js — МИНУТЫН МЭДЛЭГ (м-мэдлэг) модулийн API
 *
 * Эх сурвалж: «process flow HSD-2026.vsdx» → "м-мэдлэг" таб.
 * Backend: Resources/XAB/knowledge.py (/xab/knowledge/*).
 */
import axios from "axios";

const api = axios.create({
  baseURL: process.env.REACT_APP_API_URL || "https://hse.erdenetmc.mn/api",
});

api.interceptors.request.use((config) => {
  const token =
    localStorage.getItem("token") || sessionStorage.getItem("token");
  if (token) {
    config.headers.Authorization = `SSO ${token}`;
  }
  return config;
});

export const knowledgeApi = {
  // ── Мэдлэгийн сангийн групп (лавлах) ────────────────────────────────
  groups: () => api.get("/xab/knowledge/groups").then((r) => r.data),
  createGroup: (data) =>
    api.post("/xab/knowledge/groups", data).then((r) => r.data),
  updateGroup: (id, data) =>
    api.put(`/xab/knowledge/groups/${id}`, data).then((r) => r.data),
  deleteGroup: (id, force) =>
    api
      .delete(`/xab/knowledge/groups/${id}`, { params: force ? { force: 1 } : {} })
      .then((r) => r.data),

  // ── Группын хандах эрх (нээлттэй / хаалттай) ────────────────────────
  groupAccess: (id) =>
    api.get(`/xab/knowledge/groups/${id}/access`).then((r) => r.data),
  addAccess: (id, data) =>
    api.post(`/xab/knowledge/groups/${id}/access`, data).then((r) => r.data),
  updateAccess: (ruleId, data) =>
    api.put(`/xab/knowledge/access/${ruleId}`, data).then((r) => r.data),
  deleteAccess: (ruleId) =>
    api.delete(`/xab/knowledge/access/${ruleId}`).then((r) => r.data),

  // ── Сэдэв ────────────────────────────────────────────────────────────
  topics: (params) =>
    api.get("/xab/knowledge/topics", { params }).then((r) => r.data),
  topic: (id) => api.get(`/xab/knowledge/topics/${id}`).then((r) => r.data),
  createTopic: (data) =>
    api.post("/xab/knowledge/topics", data).then((r) => r.data),
  updateTopic: (id, data) =>
    api.put(`/xab/knowledge/topics/${id}`, data).then((r) => r.data),
  deleteTopic: (id) =>
    api.delete(`/xab/knowledge/topics/${id}`).then((r) => r.data),

  // ── Контент (бичлэг) ─────────────────────────────────────────────────
  contents: (topicId) =>
    api
      .get("/xab/knowledge/contents", { params: { topic_id: topicId } })
      .then((r) => r.data),
  createContent: (data) =>
    api.post("/xab/knowledge/contents", data).then((r) => r.data),
  updateContent: (id, data) =>
    api.put(`/xab/knowledge/contents/${id}`, data).then((r) => r.data),
  deleteContent: (id) =>
    api.delete(`/xab/knowledge/contents/${id}`).then((r) => r.data),

  /** Бичлэг/зураг MinIO-д байршуулах. onProgress(percent) — заавал биш. */
  upload: (file, onProgress) => {
    const form = new FormData();
    form.append("file", file);
    return api
      .post("/xab/knowledge/upload", form, {
        headers: { "Content-Type": "multipart/form-data" },
        onUploadProgress: (e) => {
          if (onProgress && e.total) {
            onProgress(Math.round((e.loaded * 100) / e.total));
          }
        },
      })
      .then((r) => r.data);
  },

  // ── Асуулт ───────────────────────────────────────────────────────────
  questions: (topicId) =>
    api
      .get("/xab/knowledge/questions", { params: { topic_id: topicId } })
      .then((r) => r.data),
  createQuestion: (data) =>
    api.post("/xab/knowledge/questions", data).then((r) => r.data),
  updateQuestion: (id, data) =>
    api.put(`/xab/knowledge/questions/${id}`, data).then((r) => r.data),
  deleteQuestion: (id) =>
    api.delete(`/xab/knowledge/questions/${id}`).then((r) => r.data),

  // ── Хуваарь (групп тус бүрд) ────────────────────────────────────────
  // `month` өгвөл ТЭР САРЫН хуанлид харагдах бүх долоо хоног ирнэ —
  // сарын эхний/сүүлийн мөр өөр ISO жилд багтсан ч гэсэн.
  weeks: (year, groupId, month) =>
    api
      .get("/xab/knowledge/weeks", {
        params: { year, group_id: groupId, month },
      })
      .then((r) => r.data),
  setWeek: (data) =>
    api.post("/xab/knowledge/weeks", data).then((r) => r.data),
  deleteWeek: (id) =>
    api.delete(`/xab/knowledge/weeks/${id}`).then((r) => r.data),

  // ── Ажилтны дэлгэц ───────────────────────────────────────────────────
  today: (date) =>
    api.get("/xab/knowledge/today", { params: { date } }).then((r) => r.data),
  markView: (data) =>
    api
      .post("/xab/knowledge/view", { ...data, source: "web" })
      .then((r) => r.data),
  answer: (data) =>
    api
      .post("/xab/knowledge/answer", { ...data, source: "web" })
      .then((r) => r.data),

  /** Ажилтны өөрийн түүх — үзсэн хичээл, тест, оноо (шүүлттэй). */
  myHistory: (params) =>
    api.get("/xab/knowledge/my-history", { params }).then((r) => r.data),

  // ── ХАБЭА хэлтсийн хяналт ────────────────────────────────────────────
  dashboard: (params) =>
    api.get("/xab/knowledge/dashboard", { params }).then((r) => r.data),

  /**
   * Удирдлагын тоон үзүүлэлт — сэдвийн бүлэг (групп), БН, нэгж, сэдэв,
   * долоо хоногийн чиг хандлагаар. /knowledge/analysis ба /data-analysis.
   */
  analysis: (params) =>
    api.get("/xab/knowledge/analysis", { params }).then((r) => r.data),

  /* ── QR СУВАГ ──────────────────────────────────────────────────────
   * «process flow HSD-2026.vsdx» page4 «Суралцах суваг сонгох» алхмын
   * 3 дахь суваг: «Багаж хэрэгсэл, машин техник дээр QR код байршуулна.
   * Ажилтан кодыг уншуулахад тухайн техникийг ашиглах 1 минутын
   * "Санамж видео" гарч ирнэ.»
   */
  qrList: (params) =>
    api.get("/xab/knowledge/qr", { params }).then((r) => r.data),
  qrCreate: (payload) =>
    api.post("/xab/knowledge/qr", payload).then((r) => r.data),
  qrUpdate: (id, payload) =>
    api.put(`/xab/knowledge/qr/${id}`, payload).then((r) => r.data),
  qrRemove: (id) =>
    api.delete(`/xab/knowledge/qr/${id}`).then((r) => r.data),
  /** QR уншуулахад — бичлэгийг буцааж, үзэлт бүртгэнэ (нэвтэрсэн үед) */
  qrScan: (code) =>
    api.get(`/xab/knowledge/qr/scan/${code}`).then((r) => r.data),
  /**
   * QR уншуулах — НЭВТРЭЛТГҮЙ. Зөвхөн тоног төхөөрөмжийн зааврын ГАРЧИГ
   * буцаана (файл, бичвэр, видео байхгүй). Талбар дээрх ажилтан наалт
   * уншуулахад нэвтрэхийг хүлээхгүйгээр ямар заавар мөрдөхөө харна.
   */
  qrScanPublic: (code) =>
    api.get(`/xab/knowledge/qr/public/${code}`).then((r) => r.data),
};

export default knowledgeApi;
